/**
 * PageNav — persistent sticky left navigation for rail pages.
 * Replaces the pasted-on SectionRail with an architecturally-integrated
 * navigation column: sits inside the shell container (inset from the viewport
 * edge via container padding), sticky below the global header, with a real
 * hairline boundary and an "On this page" caption.
 *
 * Typography rule (owner directive): navigation labels are PRIMARY SANS
 * (Source Sans 3) at ~13.5px — readable, human. Mono is reserved ONLY for the
 * small section numeral. This is a navigation column, not a terminal index.
 *
 * Props:
 *   items     – [{ id, num?, label }]  (flat)
 *             – [{ label, children: [{ id, num?, label }] }]  (hierarchical, e.g. Atlas)
 *   activeId  – the id of the section currently in view (scroll-spy)
 *   label     – accessible name for the nav
 */
export function PageNav({ items, activeId, label = "On this page" }) {
  return (
    <nav aria-label={label} className="hidden lg:block sticky top-[5.5rem] self-start lg:pr-8">
      <p className="font-mono text-[0.62rem] uppercase tracking-[0.22em] text-memo-muted pb-3 border-b border-memo-line">
        {label}
      </p>
      <ul className="pt-2">
        {items.map((item) => {
          if (item.children) {
            const groupActive = item.children.some((c) => c.id === activeId);
            return (
              <li key={item.label} className="pt-3">
                <p className={`font-mono text-[0.62rem] uppercase tracking-[0.18em] mb-1.5 ${groupActive ? "text-memo-signal" : "text-memo-faint"}`}>
                  {item.label}
                </p>
                <ul className="space-y-0.5 border-l border-memo-line ml-[0.4rem] pl-3">
                  {item.children.map((child) => (
                    <NavLink key={child.id} item={child} activeId={activeId} />
                  ))}
                </ul>
              </li>
            );
          }
          return <NavLink key={item.id} item={item} activeId={activeId} />;
        })}
      </ul>
    </nav>
  );
}

function NavLink({ item, activeId }) {
  const isActive = item.id === activeId;
  return (
    <li>
      <a
        href={`#${item.id}`}
        aria-current={isActive ? "true" : undefined}
        className={`pagenav-link group flex items-baseline gap-2.5 py-[0.42rem] pr-2 -ml-2 pl-2 rounded-sm ${
          isActive
            ? "bg-memo-paper text-memo-ink border-l-2 border-memo-signal -ml-2"
            : "text-memo-muted hover:text-memo-ink-2 border-l-2 border-transparent"
        }`}
      >
        {item.num ? (
          <span className="font-mono text-[0.66rem] tracking-[0.08em] text-memo-signal tabular-nums flex-shrink-0">
            {item.num}
          </span>
        ) : null}
        <span className="font-body text-[0.84rem] leading-snug">{item.label}</span>
      </a>
    </li>
  );
}
